import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Lock } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_goofy-hypatia-3/artifacts/xykys01p_Omlight%20Logo%20PNG.png";

const AdminLoginPage = () => {
  const navigate = useNavigate();
  const [isRegister, setIsRegister] = useState(false);
  const [form, setForm] = useState({ email: "", password: "" });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const endpoint = isRegister ? "/auth/register" : "/auth/login";
      const response = await axios.post(`${API}${endpoint}`, form, {
        withCredentials: true // Enable cookies
      });
      // Store token in sessionStorage (more secure than localStorage, cleared on tab close)
      sessionStorage.setItem("omlight_token", response.data.access_token);
      toast.success(isRegister ? "Account created!" : "Welcome back!");
      navigate("/admin");
    } catch (error) {
      const message = error.response?.data?.detail || "Authentication failed";
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const getButtonText = () => {
    if (loading) return <div className="spinner" />;
    return isRegister ? "Create Account" : "Sign In";
  };

  const getToggleText = () => {
    if (isRegister) return "Already have an account? Sign in";
    return "Need an account? Register";
  };

  return (
    <div
      data-testid="admin-login-page"
      className="min-h-screen bg-[#FAF9F6] flex items-center justify-center px-6"
    >
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <img
            src={LOGO_URL}
            alt="Omlight Publications"
            className="h-20 w-auto mx-auto mb-6 logo-blend"
          />
          <h1 className="font-serif text-2xl text-[#1A202C]">Admin Access</h1>
        </div>

        {/* Form Card */}
        <div className="bg-white border border-[#E2E8F0] p-8">
          <div className="flex items-center gap-2 mb-6 pb-4 border-b border-[#E2E8F0]">
            <Lock className="w-5 h-5 text-[#718096]" />
            <span className="font-sans text-sm text-[#718096]">
              {isRegister ? "Create Admin Account" : "Sign In"}
            </span>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="email" className="font-sans text-sm text-[#4A5568] mb-1.5 block">
                Email
              </Label>
              <Input
                id="email"
                data-testid="admin-email-input"
                type="email"
                placeholder="admin@example.com"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                required
                className="border-[#E2E8F0] focus:border-[#D4AF37] rounded-none"
              />
            </div>

            <div>
              <Label htmlFor="password" className="font-sans text-sm text-[#4A5568] mb-1.5 block">
                Password
              </Label>
              <Input
                id="password"
                data-testid="admin-password-input"
                type="password"
                placeholder="••••••••"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                required
                className="border-[#E2E8F0] focus:border-[#D4AF37] rounded-none"
              />
            </div>

            <Button
              type="submit"
              data-testid="admin-submit-button"
              disabled={loading}
              className="btn-gold font-sans w-full mt-6"
            >
              {getButtonText()}
            </Button>
          </form>

          <div className="mt-6 pt-4 border-t border-[#E2E8F0] text-center">
            <button
              type="button"
              data-testid="admin-toggle-mode"
              onClick={() => setIsRegister(!isRegister)}
              className="font-sans text-sm text-[#718096] hover:text-[#4A5568]"
            >
              {getToggleText()}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminLoginPage;
